import { Component, createEffect, DEV, JSX, onCleanup, untrack, useContext } from "solid-js"
import { createMutable } from "solid-js/store"
import type { Nullable } from "vite-node"
import { StateT as GeneratedStateT, StateType as GeneratedStateType } from "../../engine/bindings/typescript/tbs"
import { createCanvasElement, createOffscreenCanvas } from "../../lib/canvas/create_canvas"
import { getCanvas2dContext } from "../../lib/canvas/get_canvas_2d_context"
import { loseWebGL2Context } from "../../lib/canvas/loseWebGL2Context"
import createEngineForUI from "../../lib/core/engine/createEngineForUI"
import { Engine } from "../../lib/core/engine/Engine.type"
import { toGeneratedPlayer } from "../../lib/core/engine/engineWasmCodec"
import createDummyEntityGraphNode from "../../lib/core/engine/EntityGraphNode/createDummyEntityGraphNode"
import garbageCollectEngine from "../../lib/core/engine/garbageCollectEngine"
import getPlayerGraphNode from "../../lib/core/engine/PlayerGraphNode/getPlayerGraphNode"
import createTileGraphNodeCollection from "../../lib/core/engine/TileGraphNode/createTileGraphNodeCollection"
import type { Entity } from "../../lib/core/entity/index"
import findById from "../../lib/core/findById"
import type { Player } from "../../lib/core/player/Player.type"
import createRequestAnimationFramePromise from "../../lib/createRequestAnimationFramePromise"
import { deepClone } from "../../lib/deep_clone"
import setDuskAttributes from "../../lib/dom/setDuskAttribute"
import setStyleAttributes from "../../lib/dom/setStyleAttributes"
import Unexpected from "../../lib/Exception/Unexpected.class"
import makeCanvasIntoEntityIcon3D from "../../lib/three/fn/makeCanvasIntoEntityIcon3D"
import getFromCacheOrCreateIconOffscreenCanvas from "../../rx/memo/getFromCacheOrCreateIconOffscreenCanvas"
import { useEntityGridXYMemo } from "../../rx/memo/useEntityGridXYMemo"
import { useNodeNeighborIndexMap } from "../../rx/memo/useNodeNeighborIndexMap"
import EngineContext from "../EngineContext"

interface Props {
  entity: Entity,
  size: number,
  style?: JSX.CSSProperties,
}

const SKIP_ICON_CACHE = false

// based on SingleTileCanvas
const SingleEntityIcon3D: Component<Props> = (props: Props) => {
  // console.log('SingleEntityIcon3D', props)

  const outerEngine: Engine = useContext(EngineContext)

  // const getEntityListAtXYMap = createSharedEntityXYGridMapMemo(engine)

  const canvasElem = createCanvasElement()
  setDuskAttributes(canvasElem, 'SingleEntityIcon3D')
  canvasElem.classList.add('sprite-icon')
  canvasElem.classList.add('entity-icon')

  let prevRender: Nullable<Promise<OffscreenCanvas>> = null
  createEffect(() => {
    // console.log('SingleEntityIcon3D.effect')
    // eslint-disable-next-line solid/reactivity
    const { size, entity, style } = props
    const player: Nullable<Player> = deepClone(findById(outerEngine.state.players, entity.player_id))

    const pg0 = getPlayerGraphNode(outerEngine, entity.player_id)
    // just for tracking
    pg0()
    // console.log('pg0()', unwrap(pg0()))

    // const engine = createEngineForUI()
    const engine = createMutable<Engine>(createEngineForUI())
    if (player) {
      const bootstrapState = new GeneratedStateT()
      bootstrapState.round = 1
      bootstrapState.type = GeneratedStateType.Map
      bootstrapState.width = 1
      bootstrapState.height = 1
      bootstrapState.players = [toGeneratedPlayer(player)]
      untrack(() => {
        void engine.bootstrapWasmState(bootstrapState)
      })
    }

    // engine.state.ents = [deepClone(entity)]

    // const gridSize = useGridWidthHeight(engine)
    const getNodeNeighborIndexMap = useNodeNeighborIndexMap(engine)
    const getEntityGridXY = useEntityGridXYMemo(engine)

    const neighborMap = getNodeNeighborIndexMap()

    const tiles = createTileGraphNodeCollection(
      engine, neighborMap, getEntityGridXY, 1, 1)

    const entNode = createDummyEntityGraphNode(entity, tiles[0], pg0)

    // const pg1 = getPlayerGraph(engine, entity.player_id)
    // just for tracking
    // pg1()
    // console.log('pg1()', unwrap(pg1()))

    const p = (prevRender || createRequestAnimationFramePromise())
    p.then(() => {
      const dpr = window.devicePixelRatio || 1
      if (DEV) {
        if (!size) {
          throw new Unexpected('!size')
        }
        if (!(size % 2 === 0)) {
          throw new Unexpected('size must be even because dpr might not be whole number')
        }
        if (!entity) {
          throw new Unexpected('!entity')
        }
      }
      // const ent = unwrap(entity)
      untrack(() => {
        const sizeHiDPI = size * dpr
        canvasElem.width = canvasElem.height = sizeHiDPI
        // makeCanvasBlankAndTransparent(canvasElem)

        const sizepx = `${size}px`
        const styles = {
          ...style,
          width: sizepx,
          height: sizepx,
        }
        setStyleAttributes(canvasElem, styles)

        const key = `${player?.id || 'n'}-${player?.color1 || 'n'}-${player?.color2 || 'n'}-${size}-${dpr}-${entity.etype_id}-${entity.id}`
        // console.log('key', key)

        async function render() {
          // console.log('SingleEntityIcon3D.cacheMiss', key)
          const offscreenCanvas = createOffscreenCanvas(canvasElem.width, canvasElem.height)
          await makeCanvasIntoEntityIcon3D(engine, offscreenCanvas, entNode)
          return offscreenCanvas
        }

        if (DEV && SKIP_ICON_CACHE) {
          prevRender = render()
        } else {
          prevRender = getFromCacheOrCreateIconOffscreenCanvas(pg0, key, render)
        }

        prevRender.then((offscreenCanvas) => {
          const ctx = getCanvas2dContext(canvasElem)
          if (ctx) {
            // fix for pink background bug
            ctx.globalCompositeOperation = 'copy'
            ctx.drawImage(offscreenCanvas, 0, 0)
          }

          garbageCollectEngine(engine)
        })
      })
    })
  })

  onCleanup(() => {
    loseWebGL2Context(canvasElem)
    canvasElem.remove()
  })

  return canvasElem
}

export default SingleEntityIcon3D
